import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { HttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
  defaultDataIdFromObject,
} from 'apollo-cache-inmemory';
import getNextConfig from 'next/config';
import fetch from 'isomorphic-fetch';
import Router from 'next/router';

import { getLocalState } from '../localState';
import introspectionQueryResultData from '@g4/g4-graphql-schema/__generated/fragments.json';

import {
  AUTH_HEADER,
  TRANSACTION_HEADER,
  SYSTEM_HEADER,
  extractTokens,
  redirectToSessionTimeoutErrorPage,
} from '../../utils/authUtils';

import configureStagedTranslations from './stagedTranslations';
import stagedTranslationsJsonSchema from '../../../stagedTranslations.json';

const { NODE_ENV } = process.env;

// Polyfill fetch() on the server (used by apollo-client)
if (!process.browser) {
  global.fetch = fetch;
}

const nextConfig = getNextConfig();

const apolloLinks = {
  getHttpLink: () => {
    const customFetch = (uri, options) => {
      const { operationName } = JSON.parse(options.body);
      options.headers['X-APOLLO-OPERATION-NAME'] = operationName;
      return fetch(uri, options);
    };
    return new HttpLink({
      uri: !process.browser
        ? nextConfig.serverRuntimeConfig.graphqlEndpoint
        : nextConfig.publicRuntimeConfig.graphqlEndpoint,
      credentials: 'same-origin',
      fetch: customFetch,
    });
  },

  getHeadersLink: serverRequestContext =>
    setContext((_, { headers }) => {
      const customHeaders = {};

      if (process.browser) {
        // we need to get the transaction id from the cookie if we're in the browser
        // currently we choose not to do this server-side since we never want to query for
        // viewer-specific info during the SSR phase
        const tokens = extractTokens();
        const { transactionId } = Router.query;

        tokens.authToken =
          tokens.authToken || nextConfig.publicRuntimeConfig.authToken;

        if (tokens.authToken) {
          customHeaders[AUTH_HEADER] = tokens.authToken;
        }

        if (transactionId) {
          customHeaders[TRANSACTION_HEADER] = transactionId;
        }

        if (tokens.systemKey) {
          customHeaders[SYSTEM_HEADER] = tokens.systemKey;
        }
      } else if (serverRequestContext) {
        const {
          req: { headers },
        } = serverRequestContext;
        // varnish will attach this for internal calls
        if (headers[SYSTEM_HEADER]) {
          customHeaders[SYSTEM_HEADER] = headers[SYSTEM_HEADER];
        }
      }

      // return the headers to the context so httpLink can read them
      return {
        headers: {
          ...headers,
          ...customHeaders,
        },
      };
    }),

  getErrorsLink: () =>
    onError(({ graphQLErrors = [], networkError, operation, forward }) => {
      // if (process.env.NODE_ENV === 'development' || 1) {
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(
          `[GraphQL error]: Message: ${message}, Path: ${path}, Location: ${JSON.stringify(
            locations
          )}`
        )
      );
      networkError && console.log(`[Network error]: ${networkError}`);

      //return forward(operation);
    }),

  getSessionErrorLink: () =>
    onError(({ networkError }) => {
      if (networkError?.statusCode === 422) {
        redirectToSessionTimeoutErrorPage();
      }
    }),
};

// Build a fragment matcher from our schema's introspection data
// Helps to accurately match fragments for unions and interfaces.
// Needs to be instantiated at top level.
const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

// The ID scalars returned from the backend are not unique enough
const getDataIdFromObject = (
  localStateCustomCacheIdentfiers = {}
) => object => {
  if (
    Object.keys(localStateCustomCacheIdentfiers).includes(object.__typename)
  ) {
    return localStateCustomCacheIdentfiers[object.__typename](object);
  }

  switch (object.__typename) {
    // case 'FlightBundleAncillary':
    //   // From the schema "Type is not cacheable"
    //   return false;
    case 'FlightOption':
      // Do NOT normalize FlightOption by id.
      // FlightOption.bundleReferences are round-trip-specific — they change when
      // either leg's date changes. If normalized, the same FlightOption id (e.g.
      // '2026-03-23-G4-292') is shared across all queries, so switching the
      // departing date overwrites bundleReferences for the returning flight in the
      // shared store. When the user goes back to the original date, bundleOffers
      // is correct (stored per-query) but bundleReferences is stale (from the
      // shared normalized entry), causing a mismatch and showing wrong bundles.
      // Returning false stores each query's FlightOption independently.
      return false;
    case 'FlightBundleAncillary':
      // Bundle offers should be cached by their bundleReference + id
      return object.bundleReference && object.id
        ? `${object.__typename}:${object.bundleReference}-${object.id}`
        : false;
    case 'BundleOrderItem':
    case 'SeatOrderItem':
    case 'FlightOrderItem':
    case 'HotelOrderItem':
    case 'ItineraryAncillaryOrderItem':
    case 'TravelerAncillaryOrderItem':
    case 'VehicleOrderItem': {
      if (object.id) {
        return object.__typename + ':' + object.id;
      }

      if (NODE_ENV !== 'production') {
        console.warn(
          `Missing id field on ${object.__typename}. Make sure to query for id on any OrderItem otherwise you risk cache inconsistencies.`
        );
      }

      return false;
    }
    case 'FlightSeatmapRow':
    /* fallsthrough */
    case 'FlightSeat':
      // When returning falsey values the Apollo Cache uses identifiers from parent objects, like index
      return false;
    case 'Order': {
      // Order is only ever queried for on the client (once we have transactionId) so we're safe to use Router
      const { transactionId } = Router.query;
      return `${object.__typename}:${transactionId}`;
    }
    case 'FlightDestination':
      return null;
    case 'FlightOrigin':
      return null;
    default:
      return defaultDataIdFromObject(object);
  }
};

function getApolloClient(
  initialState = {},
  introspectionQueryResultData,
  serverRequestContext
) {
  if (process.browser && getApolloClient.Cache) {
    return getApolloClient.Cache;
  }

  const createApolloClient = (
    initialState,
    introspectionQueryResultData,
    serverRequestContext
  ) => {
    const stagedTranslations = configureStagedTranslations(
      stagedTranslationsJsonSchema,
      'DesktopBookingPath'
    );

    const localState = getLocalState();

    const cache = new InMemoryCache({
      fragmentMatcher,
      dataIdFromObject: getDataIdFromObject(localState.customCacheIdentifiers),
    }).restore(initialState);

    const client = new ApolloClient({
      name: nextConfig.publicRuntimeConfig.appName,
      version: nextConfig.publicRuntimeConfig.buildVersion,
      connectToDevTools: process.browser,
      // Disables forceFetch on the server (so queries are only run once)
      ssrMode: !process.browser,
      link: ApolloLink.from([
        apolloLinks.getErrorsLink(),
        apolloLinks.getSessionErrorLink(),
        apolloLinks.getHeadersLink(serverRequestContext),
        apolloLinks.getHttpLink(),
      ]),
      cache,
      resolvers: {
        ...stagedTranslations.resolvers,
        ...localState.resolvers,
      },
      typeDefs: [stagedTranslations.typeDefs, ...localState.typeDefsArray],
    });

    cache.writeData({
      data: localState.defaultState,
    });

    return client;
  };

  // Create a new client for every server-side request, or for
  // the inital client request.
  const apolloClient = createApolloClient(
    initialState,
    introspectionQueryResultData,
    serverRequestContext
  );

  // Reuse client when not in SSR mode.
  if (process.browser) {
    getApolloClient.Cache = apolloClient;
  }

  return apolloClient;
}

getApolloClient.Cache = null;

export default getApolloClient;
